import {
  Avatar,
  Combobox,
  ComboboxClearItem,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxOption,
  ComboboxSearch,
  ComboboxTrigger,
  FieldError,
} from '@e2n/ui/v2';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useTranslation } from 'react-i18next';
import { useAbteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useAbteilungsleiter/useAbteilungsleiter';
import { Abteilungsleiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abteilungsleiter';

export function AbteilungsleiterSelect({
  onChange,
  selectedArbeitsbereichId,
  selectedAbteilungsleiter,
  showWarning = false,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter | undefined) => void;
  selectedArbeitsbereichId: string | undefined;
  selectedAbteilungsleiter?: Abteilungsleiter;
  showWarning?: boolean;
}) {
  const { standorte } = useStandortContext();
  const standortIds = standorte.map((s) => s.id);

  if (!selectedArbeitsbereichId) {
    return (
      <AbteilungsleiterSelectContent
        showWarning={showWarning}
        abteilungsleiter={[]}
        onChange={onChange}
        selectedAbteilungsleiter={selectedAbteilungsleiter}
      />
    );
  }

  return (
    <AbteilungsleiterSelectWithData
      showWarning={showWarning}
      onChange={onChange}
      selectedArbeitsbereichId={selectedArbeitsbereichId}
      standortIds={standortIds}
      selectedAbteilungsleiter={selectedAbteilungsleiter}
    />
  );
}

function AbteilungsleiterSelectWithData({
  onChange,
  selectedArbeitsbereichId,
  standortIds,
  selectedAbteilungsleiter,
  showWarning,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter | undefined) => void;
  selectedArbeitsbereichId: string;
  standortIds: string[];
  selectedAbteilungsleiter?: Abteilungsleiter;
  showWarning: boolean;
}) {
  const { abteilungsleiter } = useAbteilungsleiter(
    standortIds,
    selectedArbeitsbereichId,
  );

  return (
    <AbteilungsleiterSelectContent
      abteilungsleiter={abteilungsleiter}
      onChange={onChange}
      selectedAbteilungsleiter={selectedAbteilungsleiter}
      showWarning={showWarning}
    />
  );
}

function AbteilungsleiterSelectContent({
  onChange,
  abteilungsleiter,
  selectedAbteilungsleiter,
  showWarning,
}: {
  onChange: (abteilungsleiter: Abteilungsleiter | undefined) => void;
  abteilungsleiter: Abteilungsleiter[];
  selectedAbteilungsleiter?: Abteilungsleiter;
  showWarning: boolean;
}) {
  const { t } = useTranslation();
  const auswahl = withSelected(abteilungsleiter, selectedAbteilungsleiter);

  const options: ComboboxOption[] = auswahl.map((a) => ({
    value: a.id,
    label: a.getName(),
    avatar: <Avatar size="xs" type="initials" initials={a.getInitials()} />,
  }));

  return (
    <div className="flex flex-col gap-1">
      {/* `modal`: liegt im Sheet, sonst scrollt die Liste nicht per Mausrad. */}
      <Combobox
        modal
        options={options}
        value={selectedAbteilungsleiter?.id ?? null}
        onValueChange={(id: string | null) =>
          onChange(auswahl.find((a) => a.id === id))
        }>
        <ComboboxTrigger placeholder={t('actions.auswaehlen')} />
        <ComboboxContent
          align="start"
          className="md:w-(--radix-popover-trigger-width)"
          title={t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.label',
          )}>
          <ComboboxSearch
            placeholder={t('domain.mitarbeiter.rotationsplan.filter.suchen')}
          />
          <ComboboxList>
            {selectedAbteilungsleiter && (
              <ComboboxClearItem>{t('actions.resetItem')}</ComboboxClearItem>
            )}
            <ComboboxEmpty
              title={
                abteilungsleiter.length > 0
                  ? t('domain.mitarbeiter.rotationsplan.filter.keineErgebnisse')
                  : t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.notFound',
                    )
              }
            />
            {options.map((option) => (
              <ComboboxItem key={option.value} option={option} />
            ))}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {showWarning && (
        <FieldError
          variant="warning"
          errors={[
            {
              message: t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.reset',
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

/** Bestehende Auswahl bleibt sichtbar, auch wenn sie nicht (mehr) in der Liste steht. */
function withSelected(
  abteilungsleiter: Abteilungsleiter[],
  selected: Abteilungsleiter | undefined,
): Abteilungsleiter[] {
  if (!selected || abteilungsleiter.some((a) => a.id === selected.id)) {
    return abteilungsleiter;
  }

  return [...abteilungsleiter, selected];
}
